// Select: native select wrapped for consistent token styling. The native
// element keeps keyboard and mobile pickers; the wrapper owns the border,
// focus ring, error state and chevron.

import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react'
import clsx from 'clsx'
import css from './Select.module.css'

export type SelectProps = {
  /** Paints the error border (sets aria-invalid when not supplied). */
  invalid?: boolean
  /** Options. */
  children?: ReactNode
  /** Class for the wrapper span, so callers can size the control. */
  className?: string
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'>

/**
 * Render a styled native select.
 * @param props - invalid flag, options, native select attributes.
 * @param ref - the native select.
 * @returns wrapper span containing the select and chevron.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid = false, className, children, ...rest },
  ref,
) {
  return (
    <span className={clsx(css.wrap, invalid && css.invalid, className)}>
      <select
        ref={ref}
        className={css.select}
        aria-invalid={invalid || rest['aria-invalid'] || undefined}
        {...rest}
      >
        {children}
      </select>
      <span className={css.chevron} aria-hidden="true">
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
    </span>
  )
})
